/**
 * Session / inactivity-timeout constants.
 *
 * These control how long a user can be idle before a warning is shown and,
 * ultimately, before the add-in logs them out automatically.  Keeping them
 * in a single file makes it easy to tune or later expose as admin config.
 */

// Webpack DefinePlugin global for idle timeout configuration
declare const __IDLE_TIMEOUT_MINS__: string | undefined;

/**
 * How long the user can be idle before the warning dialog appears (ms).
 *
 * Reads from the IDLE_TIMEOUT_MINS environment variable if set,
 * otherwise defaults to 10 minutes.
 */
export const IDLE_TIMEOUT_MS = (() => {
  // Check for environment variable (injected by webpack.DefinePlugin)
  const envMins = typeof __IDLE_TIMEOUT_MINS__ !== "undefined" ? __IDLE_TIMEOUT_MINS__ : "";
  const parsedMins = parseInt(envMins, 10);

  // Use env var if valid, otherwise default to 10 minutes
  const minutes = !isNaN(parsedMins) && parsedMins > 0 ? parsedMins : 10;

  return minutes * 60 * 1000;
})();

/** How long the warning dialog is shown before auto-logout (seconds). */
export const IDLE_WARNING_DURATION_S = 60; // 60-second countdown

/**
 * Minimum interval between activity-event resets (ms).
 *
 * mousemove fires hundreds of times per second; throttling to 2 s
 * keeps overhead negligible while still being far more granular than
 * the 15-minute idle window.
 */
export const ACTIVITY_THROTTLE_MS = 2_000;

/**
 * DOM events that count as "user activity".
 *
 * These are attached to `document` as passive listeners so they never
 * block the browser's main thread or interfere with scrolling.
 */
export const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];
